import React, { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuth } from '@/hooks/useAuth'
import { User, ChevronDown, Shield, Eye } from 'lucide-react'
import { Z_INDEX } from '@/lib/constants/z-index'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'

interface AdminUserSwitcherProps {
  onUserChange?: () => void
}

export default function AdminUserSwitcher({ onUserChange }: AdminUserSwitcherProps) {
  const { user: authUser } = useAuth()
  const { canImpersonate, isImpersonating, startImpersonation, stopImpersonation } = useImpersonation()

  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  const handleUserSelect = async (employeeId: string) => {
    if (!canImpersonate) {
      console.error('❌ User does not have permission to impersonate')
      return
    }

    const success = await startImpersonation(employeeId)

    if (success) {
      setIsOpen(false)
      onUserChange?.()
    } else {
      console.error('❌ startImpersonation failed')
    }
  }

  const handleStopImpersonation = async () => {
    if (isImpersonating) {
      await stopImpersonation()
      setIsOpen(false)
    }
  }

  // Don't render if user can't impersonate
  if (!canImpersonate && !isImpersonating) {
    return null
  }

  // Calculate dropdown position when opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      })
    }
    setIsOpen(!isOpen)
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`btn-ghost text-xs flex items-center gap-2 group ${
          isImpersonating ? 'text-warning hover:text-warning-light' : ''
        }`}
      >
        <div className="relative">
          <User className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
          {canImpersonate && (
            <Shield className="w-2 h-2 absolute -top-1 -right-1 text-warning" />
          )}
        </div>
        <span className="font-display font-medium">
          {isImpersonating ? 'Personificando' : 'Trocar Usuário'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown - Rendered as Portal */}
      {isOpen && createPortal(
        <div
          className="w-96 bg-brand-dark-light/95 backdrop-blur-sm border border-gray-700/50 rounded-monumenta shadow-monumenta-xl overflow-hidden animate-fade-in"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: Z_INDEX.DROPDOWN
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-white font-bold flex items-center gap-2">
                {canImpersonate && <Shield className="w-4 h-4 text-warning" />}
                {canImpersonate ? 'Personificação de Funcionário' : 'Ver Funcionários'}
              </h3>

              {/* Stop impersonation button */}
              {isImpersonating && (
                <button
                  onClick={handleStopImpersonation}
                  className="btn-sm btn-outline-warning"
                >
                  Sair da Personificação
                </button>
              )}
            </div>
          </div>

          {/* Search with EmployeeAutocomplete */}
          <div className="p-3">
            <EmployeeAutocomplete
              onSelect={handleUserSelect}
              navigateOnSelect={false}
              placeholder="Buscar por nome ou email..."
            />
          </div>

          {/* Footer */}
          {isImpersonating && (
            <div className="p-3 border-t border-gray-700/50 bg-brand-dark/50">
              <div className="flex items-center justify-end text-xs text-brand-gray font-body">
                <div className="flex items-center gap-1 text-warning">
                  <Eye className="w-3 h-3" />
                  <span>Personificando</span>
                </div>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
